"use client";

import { useEffect, useState } from "react";
import type { UserTagSuggestion } from "@/components/posts/user-tag-picker";
import { Label } from "@/components/ui/label";
import {
  type Participant,
  suggestionToParticipant,
  TeamCard,
} from "./base-match-template";
import { ActivityCourtPreview, ActivityDisplayPreview } from "./previews";
import { BasketballScoreEditor } from "./score-editors/basketball-score-editor";

// ──────────────────────────────────────────────────────────────────────────────
// TYPES
// ──────────────────────────────────────────────────────────────────────────────

export type BasketballTemplateData = {
  matchScore: { yourPoints: number; opponentPoints: number };
  periods?: Array<{ label: string; yourScore: number; opponentScore: number }>;
  positions: Record<string, "your-team" | "opponent-team">;
  hasScore?: boolean;
};

export type BasketballParticipant = Participant;

// ──────────────────────────────────────────────────────────────────────────────
// COMPONENT
// ──────────────────────────────────────────────────────────────────────────────

type BasketballTemplateProps = {
  currentUser: BasketballParticipant;
  onChange: (
    data: BasketballTemplateData,
    participants: BasketballParticipant[],
  ) => void;
  initialData?: Partial<BasketballTemplateData>;
};

export function BasketballTemplate({
  currentUser,
  onChange,
  initialData,
}: BasketballTemplateProps) {
  const [participants, setParticipants] = useState<BasketballParticipant[]>([
    currentUser,
  ]);
  const [teams, setTeams] = useState<
    Record<string, "your-team" | "opponent-team">
  >(() => ({
    [currentUser.id]: "your-team",
    ...(initialData?.positions ?? {}),
  }));
  type BasketballScorePart = Pick<
    BasketballTemplateData,
    "matchScore" | "periods"
  >;
  const [scorePart, setScorePart] = useState<BasketballScorePart>(() => ({
    matchScore: initialData?.matchScore ?? { yourPoints: 0, opponentPoints: 0 },
    periods: initialData?.periods ?? [],
  }));

  // Flexible team sizes (allows casual games and subs)
  const maxPerTeam = 15;

  const yourTeam = participants.filter((p) => teams[p.id] === "your-team");
  const opponentTeam = participants.filter(
    (p) => teams[p.id] === "opponent-team",
  );
  const selectedUserIds = new Set(participants.map((p) => p.id));

  useEffect(() => {
    const data: BasketballTemplateData = {
      matchScore: scorePart.matchScore,
      periods: scorePart.periods ?? [],
      positions: teams,
      hasScore: true,
    };
    onChange(data, participants);
  }, [scorePart, teams, participants, onChange]);

  const handleAddParticipant = (
    suggestion: UserTagSuggestion,
    team: "your-team" | "opponent-team",
  ) => {
    const teamCount =
      team === "your-team" ? yourTeam.length : opponentTeam.length;
    if (teamCount >= maxPerTeam) return;

    const newParticipant = suggestionToParticipant(suggestion);
    setParticipants((prev) => [...prev, newParticipant]);
    setTeams((prev) => ({ ...prev, [suggestion.id]: team }));
  };

  const handleRemoveParticipant = (userId: string) => {
    if (userId === currentUser.id) return;
    setParticipants((prev) => prev.filter((p) => p.id !== userId));
    setTeams((prev) => {
      const next = { ...prev };
      delete next[userId];
      return next;
    });
  };

  const yourPoints = scorePart.matchScore.yourPoints;
  const opponentPoints = scorePart.matchScore.opponentPoints;
  const yourWon = yourPoints > opponentPoints;
  const opponentWon = opponentPoints > yourPoints;
  const hasOpponent = opponentTeam.length > 0;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3">
        <TeamCard
          label="Team 1"
          isYourTeam
          players={yourTeam}
          maxPlayers={maxPerTeam}
          currentUserId={currentUser.id}
          selectedUserIds={selectedUserIds}
          onAddPlayer={(s) => handleAddParticipant(s, "your-team")}
          onRemovePlayer={handleRemoveParticipant}
          highlight={yourWon}
        />
        <TeamCard
          label="Team 2"
          isYourTeam={false}
          players={opponentTeam}
          maxPlayers={maxPerTeam}
          currentUserId={currentUser.id}
          selectedUserIds={selectedUserIds}
          onAddPlayer={(s) => handleAddParticipant(s, "opponent-team")}
          onRemovePlayer={handleRemoveParticipant}
          highlight={opponentWon}
        />
      </div>

      {!hasOpponent && (
        <div className="rounded-lg border border-warning/50 bg-warning/10 px-3 py-2 text-sm text-warning">
          <span>Add at least one player to Team 2</span>
        </div>
      )}

      <BasketballScoreEditor
        initialData={initialData as unknown as Record<string, unknown>}
        onChange={(data) =>
          setScorePart(data as unknown as BasketballScorePart)
        }
      />

      {participants.length >= 2 && (
        <div className="space-y-2">
          <Label className="text-text-primary">Preview</Label>
          <ActivityCourtPreview
            activityId="basketball"
            yourTeam={yourTeam}
            opponentTeam={opponentTeam}
            yourWon={yourWon}
          />
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// DISPLAY COMPONENT
// ──────────────────────────────────────────────────────────────────────────────

type BasketballDisplayProps = {
  data: BasketballTemplateData | null;
  participants: {
    id: string;
    displayName: string;
    avatarUrl: string | null;
    username: string | null;
  }[];
};

export function BasketballDisplay({
  data,
  participants,
}: BasketballDisplayProps) {
  return (
    <ActivityDisplayPreview
      activityId="basketball"
      data={data}
      participants={participants}
    />
  );
}
